import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Mail, Phone } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';

const UPDATED = 'Senast uppdaterad 4 november 2026.';

const PageLayout = ({ title, meta, children }) => {
    const navigate = useNavigate();
    return (
        <div className="kh-offer-shell kh-static-page flex flex-col">
            <PageMeta {...meta} />
            <AppMenu />
            <main className="kh-static-main flex-1">
                <div className="kh-static-inner">
                    <Button variant="outline" onClick={() => navigate(-1)} className="kh-static-back mb-4">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka
                    </Button>
                    <h1>{title}</h1>
                    <div className="kh-static-content prose max-w-none leading-relaxed">{children}</div>
                </div>
            </main>
            <AppFooter />
        </div>
    );
};

export const AboutPage = () => (
    <PageLayout
        title="Om Klart Hemma"
        meta={{ title: 'Om Klart Hemma AB – snickeri och montage i Skåne', description: 'Klart Hemma AB i Åkarp arbetar med snickeri, kök och inredningsmontage i Skåne. Så tar vi fram en prisindikation och offert.', path: '/om-oss' }}
    >
        <p className="font-semibold text-lg">Klart Hemma AB arbetar med snickeri och inredningsmontage i Skåne, med fokus på kök, badrum och andra rum i hemmet. Vi utgår från Åkarp, mellan Malmö och Lund.</p>
        <p>När ett projekt kräver el- eller VVS-arbete samordnar vi behöriga samarbetspartners, så att du har en kontakt genom hela arbetet.</p>

        <h2>Så går det till</h2>
        <ul className="list-disc pl-5 space-y-2">
            <li>Du väljer tjänst och beskriver projektet. Mått och bilder ger en mer träffsäker prisindikation.</li>
            <li>Du väljer om priset ska visas som fastpris med spann eller som löpande uppskattning.</li>
            <li>Vi stämmer av förutsättningarna med dig innan offerten blir slutgiltig.</li>
            <li>Omfattning, pris och tidplan står i offerten innan arbetet börjar.</li>
        </ul>
    </PageLayout>
);

export const ContactPage = () => (
    <PageLayout
        title="Kontakta Klart Hemma"
        meta={{ title: 'Kontakta Klart Hemma – renovering och montage i Skåne', description: 'Ring 0707 388 602 eller mejla kontakt@klarthemma.com. Eller välj tjänst och få en prisindikation direkt.', path: '/kontakt' }}
    >
        <p>Har du en fråga, eller ett projekt som inte passar någon av tjänsterna? Ring eller mejla oss så återkommer vi.</p>

        <div className="kh-contact-card">
            <a href="tel:+46707388602"><Phone className="h-5 w-5" aria-hidden="true" /> 0707 388 602</a>
            <a href="mailto:kontakt@klarthemma.com"><Mail className="h-5 w-5" aria-hidden="true" /> kontakt@klarthemma.com</a>
        </div>

        <p>Vill du se en prisbild direkt? Välj tjänst och beskriv projektet, så räknar guiden ut en prisindikation.</p>
        <a className="kh-button" href="/#tjanster">Välj tjänst <ArrowRight className="h-4 w-4" aria-hidden="true" /></a>
    </PageLayout>
);

export const TermsPage = () => (
    <PageLayout
        title="Villkor i korthet"
        meta={{ title: 'Villkor för prisindikation | Klart Hemma', description: 'Så fungerar prisindikationen i Klart Hemmas offertguide och vad som gäller vid offert.', path: '/villkor' }}
    >
        <p>Prisindikationen bygger på de uppgifter du lämnar i guiden. Ett fast pris kräver komplett underlag och bekräftas i offerten.</p>
        <p>Ändringar och tillägg som beställs under arbetets gång kan räknas som ÄTA-arbeten och debiteras enligt överenskommelse.</p>
        <p>Återbesök på grund av en arbetsplats som inte är klar, saknade leveranser eller ändrade förutsättningar ingår inte om inget annat har avtalats.</p>
        <p>Vid köksmontage rekommenderar vi att golvet är färdigt innan montaget börjar.</p>
        <p>El- och VVS-arbeten utförs av behöriga samarbetspartners. Omfattningen framgår av offerten.</p>
        <p className="text-sm text-muted-foreground">Fullständiga villkor skickas med offerten.</p>
    </PageLayout>
);

export const PrivacyPage = () => (
    <PageLayout
        title="Integritetspolicy"
        meta={{ title: 'Integritetspolicy för offertguiden | Klart Hemma', description: 'Så hanterar Klart Hemma AB personuppgifter när du använder offertguiden på offert.klarthemma.com.', path: '/integritet' }}
    >
        <p>Klart Hemma AB (organisationsnummer 559569-6351), Ostvägen 44, 232 52 Åkarp, ansvarar för de personuppgifter vi behandlar när du använder offertguiden på offert.klarthemma.com.</p>

        <h2>Uppgifter vi får av dig</h2>
        <p>Namn, telefonnummer, e-postadress, adress eller ort, mått, bilder och din beskrivning av arbetet. Lämna inte känsliga personuppgifter som inte behövs för förfrågan.</p>

        <h2>Varför vi använder uppgifterna, och med vilken rätt</h2>
        <ul className="list-disc pl-5 space-y-2">
            <li><strong>Prisindikation, offert och kontakt.</strong> Rättslig grund: åtgärder före ett avtal på din begäran.</li>
            <li><strong>Utföra och fakturera arbetet.</strong> Rättslig grund: avtal.</li>
            <li><strong>Bokföring och ROT-avdrag.</strong> Rättslig grund: rättslig förpliktelse.</li>
            <li><strong>Nyhetsbrev</strong>, bara om du kryssar i det separat. Rättslig grund: samtycke, som du kan återkalla när som helst.</li>
            <li><strong>Säkerhet och drift</strong>, till exempel serverloggar. Rättslig grund: berättigat intresse.</li>
        </ul>

        <h2>Vem som kan få uppgifterna</h2>
        <p>De som hanterar förfrågan hos oss, och samarbetspartners som behörig el- eller VVS-utförare när det krävs för offert eller arbete. Våra tekniska leverantörer behandlar uppgifter för vår räkning:</p>
        <ul className="list-disc pl-5 space-y-2">
            <li>Vercel (drift av offertguiden)</li>
            <li>MongoDB (databas för förfrågningar)</li>
            <li>Cloudinary (lagring av bilder du laddar upp)</li>
            <li>Google Workspace (e-post)</li>
            <li>ActiveCampaign (nyhetsbrev, endast om du har anmält dig)</li>
        </ul>
        <p>Några leverantörer är amerikanska företag. Överföring utanför EU/EES sker med stöd av EU–US Data Privacy Framework eller EU-kommissionens standardavtalsklausuler. Vi säljer aldrig personuppgifter.</p>

        <h2>Hur länge vi sparar uppgifterna</h2>
        <p>Förfrågningar som inte leder till ett kundprojekt gallras senast 36 månader efter den senaste kontakten. Uppgifter som behövs för ett avtal eller ett lagkrav kan sparas längre. En nyhetsbrevsanmälan sparas tills du avslutar prenumerationen.</p>

        <h2>Dina rättigheter</h2>
        <p>Du kan begära tillgång till, rättelse eller radering av dina uppgifter, invända mot viss behandling och begära begränsning. Du kan också lämna klagomål till <a href="https://www.imy.se/" target="_blank" rel="noreferrer">Integritetsskyddsmyndigheten</a>.</p>
        <p><strong>Kontakt:</strong> <a href="mailto:kontakt@klarthemma.com">kontakt@klarthemma.com</a></p>
        <p className="text-sm text-muted-foreground">{UPDATED}</p>
    </PageLayout>
);

export const CookiesPage = () => (
    <PageLayout
        title="Cookiepolicy"
        meta={{ title: 'Cookiepolicy för offertguiden | Klart Hemma', description: 'Offertguiden använder inga cookies för statistik eller annonser. Här är den lagring som förekommer.', path: '/cookies' }}
    >
        <p>Offertguiden använder inga cookies för statistik, annonsering eller spårning. Därför visar vi ingen cookiebanner.</p>

        <h2>Lagring som förekommer</h2>
        <ul className="list-disc pl-5 space-y-2">
            <li><strong>Utkast till din förfrågan.</strong> Om du börjar i snabbuppskattningen sparas dina val tillfälligt i webbläsarens lokala lagring, så att du kan fortsätta i guiden. Utkastet tas bort när det har lästs in.</li>
            <li><strong>Inloggning för personal.</strong> När någon av oss loggar in i adminpanelen sparas en inloggningsnyckel i webbläsaren. Det gäller inte besökare.</li>
        </ul>

        <h2>Varför vi inte frågar om samtycke</h2>
        <p>Enligt lagen (2022:482) om elektronisk kommunikation krävs inget samtycke för lagring som är nödvändig för en tjänst du själv har bett om, som att fylla i guiden. Om vi senare vill använda statistik eller marknadsföring frågar vi dig först.</p>

        <h2>Så rensar du</h2>
        <p>Du kan när som helst rensa webbplatsdata i webbläsarens inställningar. Guiden fungerar även om du blockerar cookies.</p>
        <p className="text-sm text-muted-foreground">{UPDATED}</p>
    </PageLayout>
);

export const UserPolicyPage = () => (
    <PageLayout
        title="Användarpolicy"
        meta={{ title: 'Användarpolicy för offertguiden | Klart Hemma', description: 'Vad som gäller när du lämnar uppgifter i Klart Hemmas offertguide.', path: '/anvandarpolicy' }}
    >
        <p>När du använder offertguiden ber vi dig att:</p>
        <ul className="list-disc pl-5 space-y-2 mt-4">
            <li>lämna korrekta uppgifter om mått, bilder och förutsättningar, så att prisindikationen blir rättvisande,</li>
            <li>inte ladda upp olagligt material eller känsliga personuppgifter som inte behövs för ärendet,</li>
            <li>komma ihåg att prisindikationen är preliminär tills underlaget är komplett och vi har stämt av förutsättningarna.</li>
        </ul>
        <p className="mt-4">Vid missbruk kan vi avstå från att hantera ärendet.</p>
    </PageLayout>
);
